import { useUserConfigurations } from "@via/api/configuration";
import {
  END_REASON_TEXT,
  refusalText,
  useHpcAppLaunches,
  useOpenHpcAppLaunch,
  useStopHpcAppLaunch,
} from "@via/pages/apps/hpc/hpc-apps.api.tsx";
import {
  type AppLaunchState,
  type HpcAppLaunchView,
  isLiveAppLaunchState,
} from "common";
import { type JSX, useState } from "react";

const STATE_TEXT: Readonly<Record<AppLaunchState, string>> = {
  requested: "Requested",
  queued: "Queued",
  starting: "Starting",
  running: "Running",
  stopping: "Stopping",
  ended: "Ended",
};

export const formatMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${minutes} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
};

/** Walltime left of a running launch, counted from when it started running. */
export const wallTimeText = (
  launch: Pick<HpcAppLaunchView, "state" | "runningAt" | "wallMinutes">,
  now: number = Date.now()
): string => {
  if (launch.state === "ended") return "";
  if (launch.state !== "running" || !launch.runningAt) {
    return `${formatMinutes(launch.wallMinutes)} once running`;
  }
  const elapsed = (now - Date.parse(launch.runningAt)) / 60000;
  const left = Math.max(0, Math.ceil(launch.wallMinutes - elapsed));
  return `${formatMinutes(left)} left`;
};

const secondaryButton =
  "rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";
const primaryButton =
  "rounded-md bg-indigo-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The user's own app launches on their clusters (agent-9-1, spec 4 steps 4 to 7). The list polls
 * every 5 seconds while any launch is live. Open is offered only while running and goes through
 * a single use URL opened at once in a new tab; Stop is offered at every live state.
 */
export default function ClusterLaunches(): JSX.Element | null {
  const { data: configurations } = useUserConfigurations();
  const enabled = Boolean(configurations?.HPC_APPS_ENABLED);
  const launches = useHpcAppLaunches(enabled);
  const stop = useStopHpcAppLaunch();
  const open = useOpenHpcAppLaunch();
  const [error, setError] = useState<string | null>(null);

  if (!enabled) return null;

  const openLaunch = async (launchId: string): Promise<void> => {
    setError(null);
    try {
      const { url } = await open.mutateAsync(launchId);
      window.open(url, "_blank", "noopener");
    } catch (failure) {
      setError(refusalText(failure, "The app could not be opened."));
    }
  };

  const stopLaunch = async (launchId: string): Promise<void> => {
    setError(null);
    try {
      await stop.mutateAsync(launchId);
    } catch (failure) {
      setError(refusalText(failure, "The app could not be stopped."));
    }
  };

  if (launches.isLoading) {
    return <p className="py-4 text-sm text-gray-600">Loading launches</p>;
  }
  if (launches.isError) {
    return (
      <p className="py-4 text-sm text-gray-600">
        Your cluster launches could not be loaded.
      </p>
    );
  }
  const rows = launches.data ?? [];

  return (
    <main className="space-y-3 py-4">
      <p className="text-sm text-gray-600">
        Apps you started on your clusters. A queued app waits in the
        cluster&apos;s queue like any job.
      </p>
      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-sm text-gray-600">
          You have not run an app on a cluster yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead>
              <tr className="text-left text-gray-900">
                <th className="px-3 py-2 font-semibold">App</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Job id</th>
                <th className="px-3 py-2 font-semibold">Resources</th>
                <th className="px-3 py-2 font-semibold">Walltime</th>
                <th className="px-3 py-2 font-semibold">Idle limit</th>
                <th className="px-3 py-2 font-semibold">Ended</th>
                <th className="px-3 py-2 font-semibold">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700">
              {rows.map(launch => (
                <tr
                  key={launch.launchId}
                  data-testid={`hpc-launch-${launch.launchId}`}>
                  <td className="px-3 py-2">{launch.appName}</td>
                  <td className="px-3 py-2">{STATE_TEXT[launch.state]}</td>
                  <td className="px-3 py-2 font-mono">
                    {launch.schedulerJobId ?? ""}
                  </td>
                  <td className="px-3 py-2">
                    {launch.cpus} cpus, {launch.memoryMb} MB
                    {launch.memoryEnforcement === "not_enforced" ? (
                      <span className="block text-xs text-gray-500">
                        Memory is not capped on this cluster
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{wallTimeText(launch)}</td>
                  <td className="px-3 py-2">
                    {formatMinutes(launch.idleMinutes)}
                  </td>
                  <td className="px-3 py-2">
                    {launch.state === "ended" && launch.endReason
                      ? END_REASON_TEXT[launch.endReason]
                      : ""}
                  </td>
                  <td className="space-x-2 whitespace-nowrap px-3 py-2 text-right">
                    {launch.state === "running" ? (
                      <button
                        type="button"
                        data-testid="hpc-launch-open"
                        className={primaryButton}
                        disabled={open.isPending}
                        onClick={() => openLaunch(launch.launchId)}>
                        Open
                      </button>
                    ) : null}
                    {isLiveAppLaunchState(launch.state) ? (
                      <button
                        type="button"
                        data-testid="hpc-launch-stop"
                        className={secondaryButton}
                        onClick={() => stopLaunch(launch.launchId)}>
                        Stop
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
